import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../Components/Sidebar';
import '../Style/Report.css';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import {
  FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaBug,
  FaBriefcase, FaFlask, FaDownload, FaSearch,
  FaFileCsv, FaFilePdf, FaCopy, FaDatabase, FaLayerGroup, FaBroom
} from 'react-icons/fa';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import CompanyLogo from '../assets/CompanyLogo.png';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/* ---------------- Constants ---------------- */
const TOP_TABS = [
  { key: 'jobs', label: 'Jobs', icon: <FaBriefcase /> },
  { key: 'failure', label: 'Failure Reports', icon: <FaBug /> },
  { key: 'tests', label: 'Test Reports', icon: <FaFlask /> }
];

const TEST_SUB_TABS = [
  { key: 'ALL', label: 'All Tests' },
  { key: 'AC_TEST', label: 'AC Test' },
  { key: 'FINAL_INSPECTION', label: 'Final Inspection' },
  { key: 'CYLINDER', label: 'Cylinder' },
  { key: 'DRIFTER', label: 'Drifter' }
];

const STATUS_COLORS = {
  Pending: '#f0ad4e',
  'In Progress': '#17a2b8',
  Completed: '#28a745',
  'On Hold': '#ffc107',
  Cancelled: '#6c757d',
  Failed: '#dc3545',
  Declined: '#dc3545',
  Draft: '#888'
};

const PIE_COLORS = [
  '#007ca8', '#00cfff', '#ffe600', '#28a745',
  '#d63384', '#6f42c1', '#fd7e14', '#20c997', '#495057'
];

/* ---------------- Helpers ---------------- */
const shortDate = d => {
  if (!d) return '—';
  try {
    if (typeof d === 'string') return d.slice(0, 10);
    if (d.toDate) return d.toDate().toISOString().slice(0, 10);
    return '—';
  } catch {
    return '—';
  }
};
const detectTestSubtype = r => r.reportType || 'UNKNOWN';

const extractACTestMetrics = r => {
  if (!r.acSections) return null;
  const findValue = label => {
    for (const sec of r.acSections) {
      for (const row of (sec.data || [])) {
        if (row.label === label) return row.value;
      }
    }
    return null;
  };
  return {
    efficiency: Number(findValue('EfficiencyPct')) || null,
    phaseA: Number(findValue('PhaseA')) || null,
    phaseB: Number(findValue('PhaseB')) || null,
    phaseC: Number(findValue('PhaseC')) || null
  };
};

const extractCylinderMetrics = r => {
  if (!r.cylinder) return null;
  const results = r.cylinder.results || [];
  const g = name => results.find(x => x.label === name)?.value;
  return {
    finalResult: g('FinalResult'),
    maxPressure: Number(g('MaxPressure')) || null
  };
};

const extractDrifterMetrics = r => {
  if (!r.drifter) return null;
  const percussionOk = (r.drifter.percussion || []).find(x => x.label === 'Percussion Tested')?.ok;
  const rotationOk = (r.drifter.rotation || []).find(x => x.label === 'Rotation Tested')?.ok;
  return { percussionOk: !!percussionOk, rotationOk: !!rotationOk };
};

/* ---------------- EXPORT HELPERS ---------------- */
const toCSV = (rows, columns) => {
  const header = columns.map(c => `"${c.label}"`).join(',');
  const body = rows.map(r =>
    columns.map(c => {
      const v = c.accessor(r);
      return `"${(v ?? '').toString().replace(/"/g, '""')}"`;
    }).join(',')
  );
  return [header, ...body].join('\n');
};

const downloadFile = (content, name, type='text/csv') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const generatePDF = async ({ title, columns, rows }) => {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  let y = 36;
  try {
    const img = new Image();
    img.src = CompanyLogo;
    await new Promise(res => { img.onload = res; img.onerror = res; });
    pdf.addImage(img, 'PNG', 40, y, 58, 58);
  } catch {}
  pdf.setFont('Helvetica', 'bold').setFontSize(16).text(title, 110, y + 30);
  y += 78;
  autoTable(pdf, {
    startY: y,
    head: [columns.map(c => c.label)],
    body: rows.map(r => columns.map(c => {
      const v = c.accessor(r);
      return v == null ? '' : String(v);
    })),
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [0, 0, 0], textColor: 255 }
  });
  pdf.save(`${title.replace(/\s+/g,'_')}_${Date.now()}.pdf`);
};

/* ---------------- Component ---------------- */
export default function Report() {
  const [activeTab, setActiveTab] = useState('jobs');
  const [testSubTab, setTestSubTab] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [jobs, setJobs] = useState([]);
  const [fails, setFails] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Fetch Data */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const jobSnap = await getDocs(query(collection(db, 'Jobs'), orderBy('createdAt', 'desc')));
        const failSnap = await getDocs(query(collection(db, 'FailureReports'), orderBy('createdAt', 'desc')));
        const testSnap = await getDocs(query(collection(db, 'TestReports'), orderBy('createdAt', 'desc')));
        setJobs(jobSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setFails(failSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTests(testSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        toast.error('Failed to fetch report data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const baseData = activeTab === 'jobs' ? jobs : activeTab === 'failure' ? fails : tests;

  const filtered = useMemo(() => {
    let arr = [...baseData];
    if (activeTab === 'tests' && testSubTab !== 'ALL') {
      arr = arr.filter(t => detectTestSubtype(t) === testSubTab);
    }
    arr = arr.filter(r => {
      const d = shortDate(r.header?.date || r.date || r.createdAt);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
      if (statusFilter && (r.header?.status || r.status || '') !== statusFilter) return false;
      if (search) {
        const hay = JSON.stringify(r).toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
    return arr;
  }, [baseData, activeTab, testSubTab, dateFrom, dateTo, statusFilter, search]);

  const statuses = useMemo(() => {
    const set = new Set();
    baseData.forEach(r => set.add(r.header?.status || r.status || '—'));
    return Array.from(set).filter(s => s && s !== '—');
  }, [baseData]);

  /* KPIs */
  const kpis = useMemo(() => {
    if (activeTab === 'jobs') {
      const total = filtered.length;
      const completed = filtered.filter(j => (j.status === 'Completed') || (j.header?.status === 'Completed')).length;
      const pending = filtered.filter(j => (j.status === 'Pending') || (j.header?.status === 'Pending')).length;
      const inProgress = filtered.filter(j => (j.status === 'In Progress') || (j.header?.status === 'In Progress')).length;
      const customers = new Set(filtered.map(j => j.customer || j.header?.customer)).size;
      return [
        { icon:<FaBriefcase/>, title:'Total Jobs', value: total },
        { icon:<FaCheckCircle/>, title:'Completed', value: completed },
        { icon:<FaClock/>, title:'In Progress', value: inProgress },
        { icon:<FaUsers/>, title:'Unique Customers', value: customers },
        { icon:<FaTimesCircle/>, title:'Pending', value: pending }
      ];
    }
    if (activeTab === 'failure') {
      const total = filtered.length;
      const equipment = new Set(filtered.map(f => f.equipment)).size;
      const completed = filtered.filter(f => (f.status === 'Completed') || (f.header?.status === 'Completed')).length;
      const pending = filtered.filter(f => (f.status === 'Pending') || (f.header?.status === 'Pending')).length;
      const customers = new Set(filtered.map(f => f.customer)).size;
      return [
        { icon:<FaBug/>, title:'Failure Reports', value: total },
        { icon:<FaCheckCircle/>, title:'Completed', value: completed },
        { icon:<FaClock/>, title:'Pending', value: pending },
        { icon:<FaUsers/>, title:'Customers', value: customers },
        { icon:<FaLayerGroup/>, title:'Equipment Types', value: equipment }
      ];
    }
    // Tests
    const total = filtered.length;
    const completed = filtered.filter(t => (t.header?.status || t.status) === 'Completed').length;
    const customers = new Set(filtered.map(t => t.header?.customer || t.customer)).size;

    let avgEff = null, acCount=0;
    filtered.forEach(r => {
      if (detectTestSubtype(r) === 'AC_TEST') {
        const m = extractACTestMetrics(r);
        if (m?.efficiency != null) { avgEff = (avgEff || 0) + m.efficiency; acCount++; }
      }
    });
    if (avgEff != null && acCount) avgEff = (avgEff / acCount).toFixed(1);

    return [
      { icon:<FaFlask/>, title:'Test Reports', value: total },
      { icon:<FaCheckCircle/>, title:'Completed', value: completed },
      { icon:<FaUsers/>, title:'Customers', value: customers },
      { icon:<FaDatabase/>, title:'Distinct Types', value: TEST_SUB_TABS.filter(t=>t.key!=='ALL').reduce((a,t)=> a + (filtered.some(r=>detectTestSubtype(r)===t.key)?1:0),0) },
      { icon:<FaCheckCircle/>, title:'Avg AC Efficiency %', value: avgEff ?? '—' }
    ];
  }, [activeTab, filtered]);

  /* Chart Data */
  const timeSeries = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const d = shortDate(r.header?.date || r.date || r.createdAt);
      if (!d || d === '—') return;
      map[d] = (map[d] || 0) + 1;
    });
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b))
      .map(([date,count]) => ({ date, count }));
  }, [filtered]);

  const cumulativeData = useMemo(() => {
    let cum = 0;
    return timeSeries.map(d => ({ date: d.date, cumulative: (cum += d.count) }));
  }, [timeSeries]);

  const statusDist = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const st = r.header?.status || r.status || 'Unknown';
      map[st] = (map[st] || 0) + 1;
    });
    return Object.entries(map).map(([name,value]) => ({ name, value }));
  }, [filtered]);

  const customerDist = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const c = r.header?.customer || r.customer || 'Unknown';
      map[c] = (map[c] || 0) + 1;
    });
    return Object.entries(map).map(([name,value]) => ({ name, value }));
  }, [filtered]);

  const stackedData = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const d = shortDate(r.header?.date || r.date || r.createdAt);
      const st = r.header?.status || r.status || 'Unknown';
      if (!d) return;
      if (!map[d]) map[d] = { date: d };
      map[d][st] = (map[d][st] || 0) + 1;
    });
    return Object.values(map).sort((a,b)=>a.date.localeCompare(b.date));
  }, [filtered]);

  const acAnalytics = useMemo(() => {
    if (activeTab !== 'tests' || testSubTab !== 'AC_TEST') return [];
    return filtered.filter(r => detectTestSubtype(r) === 'AC_TEST')
      .map(r => {
        const m = extractACTestMetrics(r) || {};
        return {
          reportId: r.reportId,
            efficiency: m.efficiency,
            phaseA: m.phaseA, phaseB: m.phaseB, phaseC: m.phaseC
        };
      }).filter(x => x.efficiency!=null || x.phaseA!=null);
  }, [filtered, activeTab, testSubTab]);

  const cylinderAnalytics = useMemo(() => {
    if (activeTab !== 'tests' || testSubTab !== 'CYLINDER') return [];
    return filtered.filter(r => detectTestSubtype(r) === 'CYLINDER')
      .map(r => {
        const c = extractCylinderMetrics(r) || {};
        return { reportId: r.reportId, maxPressure: c.maxPressure };
      });
  }, [filtered, activeTab, testSubTab]);

  const drifterAnalytics = useMemo(() => {
    if (activeTab !== 'tests' || testSubTab !== 'DRIFTER') return [];
    return filtered.filter(r => detectTestSubtype(r) === 'DRIFTER')
      .map(r => {
        const m = extractDrifterMetrics(r) || {};
        return { reportId: r.reportId, percussion: m.percussionOk?1:0, rotation: m.rotationOk?1:0 };
      });
  }, [filtered, activeTab, testSubTab]);

  const columns = useMemo(() => {
    if (activeTab === 'jobs') {
      return [
        { label:'Job ID', accessor:r=>r.jobId || '—' },
        { label:'Name', accessor:r=>r.jobName || '—' },
        { label:'Customer', accessor:r=>r.customer || '—' },
        { label:'Status', accessor:r=>r.status || r.header?.status || '—' },
        { label:'Date', accessor:r=>shortDate(r.createdAt) }
      ];
    }
    if (activeTab === 'failure') {
      return [
        { label:'Report ID', accessor:r=>r.reportId || '—' },
        { label:'Job', accessor:r=>r.jobId || '—' },
        { label:'Customer', accessor:r=>r.customer || '—' },
        { label:'Equipment', accessor:r=>r.equipment || '—' },
        { label:'Status', accessor:r=>r.status || r.header?.status || '—' },
        { label:'Date', accessor:r=>shortDate(r.createdAt) }
      ];
    }
    return [
      { label:'Report ID', accessor:r=>r.reportId || '—' },
      { label:'Type', accessor:r=>detectTestSubtype(r) },
      { label:'Customer', accessor:r=>r.header?.customer || r.customer || '—' },
      { label:'Job', accessor:r=>r.header?.jobId || r.jobId || '—' },
      { label:'Status', accessor:r=>r.header?.status || r.status || '—' },
      { label:'Date', accessor:r=>shortDate(r.header?.date || r.createdAt) }
    ];
  }, [activeTab]);

  /* Exports */
  const exportCSV = () => {
    if (!filtered.length) return toast.info('No data to export.');
    const csv = toCSV(filtered, columns);
    downloadFile(csv, `${activeTab}_${testSubTab}_${Date.now()}.csv`);
    toast.success('CSV exported');
  };
  const exportPDF = () => {
    if (!filtered.length) return toast.info('No data to export.');
    generatePDF({
      title: `${activeTab === 'tests' ? 'Test Reports' : activeTab === 'failure' ? 'Failure Reports' : 'Jobs'} ${testSubTab !== 'ALL' ? '('+testSubTab+')' : ''}`,
      columns,
      rows: filtered
    }).then(()=>toast.success('PDF exported'));
  };
  const copySummary = () => {
    if (!filtered.length) return toast.info('Nothing to summarize.');
    const first = filtered[0];
    const summary = `Summary (${activeTab}${activeTab==='tests'?' / '+testSubTab:''}):
Count: ${filtered.length}
First Record: ${JSON.stringify(first, null, 2)}`;
    navigator.clipboard.writeText(summary)
      .then(()=>toast.success('Summary copied'))
      .catch(()=>toast.error('Copy failed'));
  };
  const copyJSON = () => {
    if (!filtered.length) return toast.info('No data.');
    navigator.clipboard.writeText(JSON.stringify(filtered, null, 2))
      .then(()=>toast.success('JSON copied'))
      .catch(()=>toast.error('Copy failed'));
  };

  const clearAllFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setStatusFilter('');
    if (activeTab === 'tests') setTestSubTab('ALL');
  };

  /* UI Subcomponents */
  const StatusChips = () => (
    <div className="status-chip-row">
      {statuses.map(st => {
        const active = statusFilter === st;
        const color = STATUS_COLORS[st] || '#999';
        const count = filtered.filter(r => (r.header?.status || r.status) === st).length;
        if (!count) return null;
        return (
          <button
            key={st}
            className={`status-chip ${active ? 'active' : ''}`}
            style={{ '--chip-color': color }}
            onClick={() => setStatusFilter(active ? '' : st)}
          >
            {st} <span className="mini-count">{count}</span>
          </button>
        );
      })}
      {(statusFilter || dateFrom || dateTo || search || (activeTab==='tests' && testSubTab!=='ALL')) && (
        <button className="status-chip reset" onClick={clearAllFilters} title="Clear all filters">
          <FaBroom /> Reset
        </button>
      )}
    </div>
  );

  const Charts = () => (
    <div className="charts-grid">
      <div className="chart-card">
        <h3>Daily Volume</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={timeSeries}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date"/>
            <YAxis allowDecimals={false}/>
            <Tooltip />
            <Bar dataKey="count" fill="var(--aqua-400)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card">
        <h3>Cumulative Total</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={cumulativeData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date"/>
            <YAxis allowDecimals={false}/>
            <Tooltip />
            <Line type="monotone" dataKey="cumulative" stroke="var(--aqua-700)" strokeWidth={2}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card">
        <h3>Status Distribution</h3>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie data={statusDist} dataKey="value" nameKey="name" outerRadius={80} label>
              {statusDist.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]} />)}
            </Pie>
            <Legend verticalAlign="bottom"/>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card">
        <h3>Per Customer Share</h3>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie data={customerDist} dataKey="value" nameKey="name" outerRadius={80} label>
              {customerDist.map((_,i)=><Cell key={i} fill={PIE_COLORS[(i+3)%PIE_COLORS.length]} />)}
            </Pie>
            <Legend verticalAlign="bottom"/>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-card wide">
        <h3>Stacked By Status / Day</h3>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={stackedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date"/>
            <YAxis allowDecimals={false}/>
            <Tooltip />
            {statuses.map((st,i)=>(
              <Area
                key={st}
                type="monotone"
                dataKey={st}
                stackId="1"
                stroke={STATUS_COLORS[st]||PIE_COLORS[i%PIE_COLORS.length]}
                fill={STATUS_COLORS[st]||PIE_COLORS[i%PIE_COLORS.length]}
                fillOpacity={0.8}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {activeTab === 'tests' && testSubTab === 'AC_TEST' && !!acAnalytics.length && (
        <div className="chart-card wide">
          <h3>AC Test Currents & Efficiency</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={acAnalytics}>
                <CartesianGrid strokeDasharray="3 3"/>
                <XAxis dataKey="reportId" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="phaseA" name="Phase A" stroke="#007ca8"/>
                <Line type="monotone" dataKey="phaseB" name="Phase B" stroke="#00cfff"/>
                <Line type="monotone" dataKey="phaseC" name="Phase C" stroke="#ffe600"/>
                <Line type="monotone" dataKey="efficiency" name="Efficiency %" stroke="#28a745" strokeWidth={2}/>
              </LineChart>
            </ResponsiveContainer>
        </div>
      )}

      {activeTab === 'tests' && testSubTab === 'CYLINDER' && !!cylinderAnalytics.length && (
        <div className="chart-card">
          <h3>Cylinder Max Pressure (Bar)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={cylinderAnalytics}>
              <CartesianGrid strokeDasharray="3 3"/>
              <XAxis dataKey="reportId" />
              <YAxis/>
              <Tooltip />
              <Bar dataKey="maxPressure" fill="var(--aqua-700)"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {activeTab === 'tests' && testSubTab === 'DRIFTER' && !!drifterAnalytics.length && (
        <div className="chart-card">
          <h3>Drifter Test Pass Rates</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={[
              {
                metric:'Percussion Pass Rate',
                value: drifterAnalytics.reduce((a,c)=>a+(c.percussion?c.percussion:0),0)/drifterAnalytics.length*100
              },
              {
                metric:'Rotation Pass Rate',
                value: drifterAnalytics.reduce((a,c)=>a+(c.rotation?c.rotation:0),0)/drifterAnalytics.length*100
              }
            ]}>
              <CartesianGrid strokeDasharray="3 3"/>
              <XAxis dataKey="metric"/>
              <YAxis domain={[0,100]} />
              <Tooltip formatter={(v)=>v.toFixed(1)+'%'}/>
              <Bar dataKey="value" fill="var(--aqua-400)">
                <Cell key="p" fill={PIE_COLORS[2]} />
                <Cell key="r" fill={PIE_COLORS[5]} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );

  const Table = () => (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map(c => <th key={c.label}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {filtered.map(r => (
            <tr key={r.id}>
              {columns.map(c => {
                const val = c.accessor(r);
                if (c.label === 'Status') {
                  const color = STATUS_COLORS[val] || '#777';
                  return (
                    <td key={c.label}>
                      <span className="status-pill" style={{ background: color+'22', color }}>
                        {val}
                      </span>
                    </td>
                  );
                }
                return <td key={c.label}>{val}</td>;
              })}
            </tr>
          ))}
          {!filtered.length && (
            <tr>
              <td colSpan={columns.length} className="empty">
                No records match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="report-page">
      <ToastContainer position="top-right" autoClose={2500}/>
      <Sidebar />
      <main className="report-main">
        <header className="report-header">
          <h1>Analytics & Reports</h1>
          <div className="top-tabs" role="tablist">
            {TOP_TABS.map(t => (
              <button
                key={t.key}
                role="tab"
                aria-selected={activeTab === t.key}
                className={`top-tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab(t.key);
                  setStatusFilter('');
                  setTestSubTab('ALL');
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </header>

        {activeTab === 'tests' && (
          <div className="sub-tabs" role="tablist">
            {TEST_SUB_TABS.map(s => (
              <button
                key={s.key}
                role="tab"
                aria-selected={testSubTab === s.key}
                className={`sub-tab ${testSubTab === s.key ? 'active' : ''}`}
                onClick={() => setTestSubTab(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Compact Inline Filters */}
        <section className="filters-bar sticky">
          <div className="inline-filters">
            <div className="search-combo">
              <FaSearch className="search-icon" />
              <input
                className="search-field"
                placeholder="Search…"
                value={search}
                onChange={e=>setSearch(e.target.value)}
                aria-label="Search"
              />
            </div>
            <div className="date-inline">
              <label>
                <span>From</span>
                <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} />
              </label>
              <label>
                <span>To</span>
                <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} />
              </label>
            </div>
            <div className="export-group">
              <button className="btn export" onClick={exportCSV} title="Export CSV"><FaFileCsv/> CSV</button>
              <button className="btn export" onClick={exportPDF} title="Export PDF"><FaFilePdf/> PDF</button>
              <button className="btn export" onClick={copySummary} title="Copy Summary"><FaCopy/> Summary</button>
              <button className="btn export" onClick={copyJSON} title="Copy JSON"><FaCopy/> JSON</button>
              <button className="btn export subtle" onClick={clearAllFilters} title="Clear all filters"><FaBroom/></button>
            </div>
          </div>
        </section>

        <StatusChips />

        <section className="kpi-grid">
          {kpis.map(k => (
            <div key={k.title} className="kpi-card" tabIndex={0} aria-label={`${k.title} ${k.value}`}>
              <div className="kpi-icon">{k.icon}</div>
              <div className="kpi-meta">
                <span className="kpi-title">{k.title}</span>
                <span className="kpi-value">{k.value}</span>
              </div>
            </div>
          ))}
        </section>

        {loading ? (
          <div className="loading-block">
            <div className="spinner" />
            <p>Loading analytics…</p>
          </div>
        ) : (
          <>
            <Charts />
            <div className="table-header-row">
              <h2 className="table-title">
                {activeTab === 'jobs' ? 'Jobs'
                  : activeTab === 'failure' ? 'Failure Reports'
                    : 'Test Reports'}
              </h2>
              <span className="record-count-badge">{filtered.length} record{filtered.length!==1?'s':''}</span>
            </div>
            <Table />
          </>
        )}
      </main>
    </div>
  );
}
